import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { withSpacing, clsx, SEMANTIC_COLORS, TYPE_COLORS } from '@wds/utils';
import { createUseStyles, useTheme } from 'react-jss';

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var objectWithoutPropertiesLoose = _objectWithoutPropertiesLoose;

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = objectWithoutPropertiesLoose(source, excluded);
  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

var objectWithoutProperties = _objectWithoutProperties;

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

var defineProperty = _defineProperty;

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

const useStyles = createUseStyles(theme => ({
  wdsTypography: ({
    align,
    color,
    variant
  }) => {
    const typeColor = theme.typeColor,
          semanticColor = theme.semanticColor,
          fontFamily = theme.fontFamily,
          fontSize = theme.fontSize,
          fontWeight = theme.fontWeight;
    const chosenColor = typeColor[color] || semanticColor[color];
    return _objectSpread(_objectSpread({
      fontFamily: fontFamily.base,
      color: chosenColor,
      textAlign: align,
      margin: 0
    }, variant && variant !== 'link' && {
      fontSize: fontSize[variant],
      fontWeight: {
        hero1: fontWeight.light,
        hero2: fontWeight.light,
        title: fontWeight.light,
        sectionTitle: fontWeight.light,
        cardTitle: fontWeight.medium,
        body: fontWeight.regular,
        bodySm: fontWeight.regular
      }[variant]
    }), variant && variant === 'link' && {
      color: typeColor.link,
      textDecoration: 'none',
      border: 'none',
      background: 'none',
      outline: 'none',
      cursor: 'pointer',
      fontWeight: 'inherit',
      fontSize: 'inherit',
      '&:hover, &:focus': {
        textDecoration: 'underline'
      }
    });
  }
}));

const alignment = Object.freeze(['center', 'left', 'right']);
const variants = Object.freeze(['body', 'bodySm', 'cardTitle', 'hero1', 'hero2', 'link', 'pageTitle', 'sectionTitle']); // @todo: custom components wanted like <Link />??

const componentList = Object.freeze(['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a', 'p', 'span', 'div', 'article', 'section']);
const Typography = /*#__PURE__*/forwardRef((_ref, ref) => {
  let align = _ref.align,
      className = _ref.className,
      children = _ref.children,
      color = _ref.color,
      component = _ref.component,
      variant = _ref.variant,
      rest = objectWithoutProperties(_ref, ["align", "className", "children", "color", "component", "variant"]);

  const Component = component;
  const theme = useTheme();
  const classes = useStyles({
    align,
    color,
    variant,
    theme
  });
  const classNames = clsx(className, classes.wdsTypography);
  return /*#__PURE__*/React.createElement(Component, Object.assign({
    className: classNames
  }, rest, {
    ref: ref
  }), children);
});
Typography.displayName = 'Typography';
Typography.propTypes = {
  align: PropTypes.oneOf(alignment),
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  color: PropTypes.oneOf(SEMANTIC_COLORS.concat(TYPE_COLORS)),
  component: PropTypes.oneOf(componentList),
  variant: PropTypes.oneOf(variants)
};
Typography.defaultProps = {
  align: 'left',
  className: undefined,
  component: 'div',
  color: 'dark',
  variant: 'body'
};
Typography.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "Typography",
  "props": {
    "align": {
      "defaultValue": {
        "value": "'left'",
        "computed": false
      },
      "type": {
        "name": "enum",
        "computed": true,
        "value": "alignment"
      },
      "required": false,
      "description": ""
    },
    "className": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "string"
      },
      "required": false,
      "description": ""
    },
    "component": {
      "defaultValue": {
        "value": "'div'",
        "computed": false
      },
      "type": {
        "name": "enum",
        "computed": true,
        "value": "componentList"
      },
      "required": false,
      "description": ""
    },
    "color": {
      "defaultValue": {
        "value": "'dark'",
        "computed": false
      },
      "type": {
        "name": "enum",
        "computed": true,
        "value": "SEMANTIC_COLORS.concat(TYPE_COLORS)"
      },
      "required": false,
      "description": ""
    },
    "variant": {
      "defaultValue": {
        "value": "'body'",
        "computed": false
      },
      "type": {
        "name": "enum",
        "computed": true,
        "value": "variants"
      },
      "required": false,
      "description": ""
    },
    "children": {
      "type": {
        "name": "node"
      },
      "required": true,
      "description": ""
    }
  }
};
var Typography$1 = withSpacing(Typography);

export { Typography$1 as Typography };
